import type { RouteRecordRaw } from "vue-router";

import authRoutes from "./auth/index";
import coreRoutes from "./core/index";
import { RouteNames } from "./types/names";
import type { AppRouteRecordRaw } from "./types/types";
import { filterRoutesByPermissions, routesToMenuItems } from "./utils/utils";

import { useAuthStore } from "@/features/auth/store/authStore";

/**
 * All application routes
 */
const modules: Array<AppRouteRecordRaw> = [
  // Root route - handled by authGuard which redirects to first accessible route
  // Component is required because redirect happens BEFORE guards
  {
    path: "/",
    name: "Root",
    meta: {
      title: "Home",
      showInMenu: false,
      isRootRedirect: true, // Special flag to trigger smart redirect in guard
    },
  },

  // 403 Forbidden page (public)
  {
    path: "/403",
    name: "Forbidden",
    component: () => import("@/pages/ForbiddenAccess.vue"),
    meta: {
      title: "403 Forbidden",
      layout: "empty",
      showInMenu: false,
    },
  },
  // Feature routes
  ...coreRoutes,
  ...authRoutes,
  {
    path: "/:pathMatch(.*)*",
    name: RouteNames.NOT_FOUND,
    component: () => import("@/pages/NotFound.vue"),
    meta: {
      title: "404 Not Found",
      showInMenu: false,
    },
  },
];

/**
 * Routes that should appear in main menu
 * Automatically filtered from modules based on meta.showInMenu
 */
export const menuModules = modules.filter(
  (route) => route.meta?.showInMenu !== false && route.name,
);

/**
 * Get menu items filtered by current user permissions
 * Call this function with authStore to get fresh menu items based on user's current permissions
 * Use in computed properties for reactivity
 * @param authStore - Auth store instance
 * @returns Filtered menu items based on user permissions
 */
export function getMenuItems(authStore: ReturnType<typeof useAuthStore>) {
  return routesToMenuItems(
    filterRoutesByPermissions(menuModules as RouteRecordRaw[], authStore),
  );
}

export default modules as Array<RouteRecordRaw>;
